import React, { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import FormModal from '@shared/ui/FormModal';

interface TypeToConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmationWord: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const TypeToConfirmDialog: React.FC<TypeToConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmationWord,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isLoading = false
}) => {
  const [inputValue, setInputValue] = useState('');
  
  // Reset input when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setInputValue('');
    }
  }, [isOpen]);

  const isConfirmEnabled = inputValue === confirmationWord;

  const handleConfirm = () => {
    if (isConfirmEnabled && !isLoading) {
      onConfirm();
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      title={title}
      onClose={onCancel}
      onSubmit={handleConfirm}
      size="sm"
      submitLabel={confirmText}
      cancelLabel={cancelText}
      submitVariant="danger"
      isSubmitting={isLoading}
      submitDisabled={!isConfirmEnabled}
      // Anything typed here is a deliberate confirmation of something
      // destructive; a stray backdrop click should not discard it.
      isDirty={inputValue.length > 0}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle
          className="mt-0.5 h-6 w-6 shrink-0 text-error-600 dark:text-error-400"
          aria-hidden="true"
        />
        <p className="text-text-secondary dark:text-text-dark-secondary">{message}</p>
      </div>

      <div className="mt-4">
        <label
          htmlFor="confirmation-input"
          className="mb-2 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary"
        >
          Type{' '}
          <span className="font-bold text-error-600 dark:text-error-400">{confirmationWord}</span>{' '}
          to confirm
        </label>
        {/* Enter submits via the dialog's <form>; Escape is the dialog's. */}
        <input
          id="confirmation-input"
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          className="input w-full"
          placeholder={`Type "${confirmationWord}" here`}
          autoComplete="off"
          autoFocus
          disabled={isLoading}
        />
      </div>
    </FormModal>
  );
};

export default TypeToConfirmDialog;










